import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import { Tool, ToolSlot } from 'cd-eft/components/Design/BreaklineTools/framework/Tool';
import { esriGeometryEngine, esriProjection, Geometry, Polygon, Polyline } from 'cd-eft/utils/esriTypes';
import { computeAcres, explodeMultipartPolygon } from 'cd-eft/utils/gisUtils';
import { DrawPolylineInputTask } from '../EditSoilAndLand/EFT-851/DrawPolylineInputTask';

export class WatershedSmartCutTool extends Tool {
  private readonly mapView: MapView;
  private task: DrawPolylineInputTask;
  private watershedPolygonLayer: GraphicsLayer;
  private outletPolylineLayer: GraphicsLayer;
  private errorReset: () => void;
  private mapContainer: HTMLElement;

  private watershedModified: boolean = false;

  public wasPolygonsDeleted(): boolean {
    return this.watershedModified;
  }

  constructor(mapView: MapView, toolSlot: ToolSlot, watershedPolygonLayer: GraphicsLayer, outletPolylineLayer: GraphicsLayer, callback: () => void) {
    super('Split Watershed Polygon', 'single', toolSlot);
    this.mapView = mapView;
    this.task = new DrawPolylineInputTask(this.mapView, { allowSelfIntersecting: false });
    this.watershedPolygonLayer = watershedPolygonLayer;
    this.outletPolylineLayer = outletPolylineLayer;
    this.errorReset = callback;
    this.mapContainer = this.mapView.container as HTMLElement;

    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  private handleOutsideClick = (event: MouseEvent): void => {
    if (!this.mapContainer.contains(event.target as Node)) {
      this.deactivate();
    }
  };

  public async deactivate(): Promise<void> {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
  }

  protected async onExecuteOperation(): Promise<void> {
    const result = await this.task.executeTaskAsync();

    let cuttingLine: Polyline | undefined = undefined;

    const splitGraphics: Graphic[] = [];
    const splitShapes: Polygon[][] = [];
    let maxObjectId = -1;

    let outletPolyline = this.outletPolylineLayer.graphics.getItemAt(0)?.geometry;
    let spatialReference: any;

    if (!outletPolyline) {
      // console.error('No outlet polyline found.');
      return;
    }

    for (const graphic of this.watershedPolygonLayer.graphics) {
      if (graphic.attributes.OBJECTID > maxObjectId) {
        maxObjectId = graphic.attributes.OBJECTID;
      }
      const polygon = graphic.geometry as Polygon;
      spatialReference = polygon.spatialReference;
      if (!cuttingLine || cuttingLine.spatialReference.wkid !== polygon.spatialReference.wkid) {
        cuttingLine = esriProjection.project(result, polygon.spatialReference) as Polyline;
      }
      if (esriGeometryEngine.crosses(cuttingLine, polygon)) {
        const polygons = esriGeometryEngine.cut(polygon, cuttingLine) as Polygon[];
        
        if (polygons.length >= 2) {
          splitGraphics.push(graphic);
          splitShapes.push(polygons);
          this.watershedModified = true;
        }
      }
    }

    if (!outletPolyline || outletPolyline.spatialReference.wkid !== spatialReference.wkid) {
      outletPolyline = esriProjection.project(outletPolyline, spatialReference) as Geometry;
    }

    for (const graphic of splitGraphics) {
      const index = splitGraphics.indexOf(graphic);
      const shapes = splitShapes[index];
      let origIndex = this.watershedPolygonLayer.graphics.indexOf(graphic);
      let origIdTaken = false;

      for (const shape of shapes) {
        if (shape.rings.length > 1) {
          const shapes2 = explodeMultipartPolygon(shape);
          for (const shape2 of shapes2) {
            if (esriGeometryEngine.contains(shape2, outletPolyline)) {
              const newGraphic = new Graphic();
              newGraphic.symbol = graphic.symbol;
              newGraphic.geometry = shape2;
              newGraphic.attributes = {
                Acres: computeAcres(shape2),
                Avg_Slope: graphic.attributes['Avg_Slope'],
                MaxFlowLenghtFt: graphic.attributes['MaxFlowLenghtFt'],
                OBJECTID: !origIdTaken ? graphic.attributes.OBJECTID : ++maxObjectId,
                Shape_Area: esriGeometryEngine.geodesicArea(shape2, "square-meters"),
                Shape_Length: esriGeometryEngine.geodesicLength(shape2, "meters"),
                Subbasin: graphic.attributes['Subbasin'],
                gridcode: graphic.attributes['gridcade']
              };
              origIdTaken = true;
              this.watershedPolygonLayer.graphics.add(newGraphic, origIndex++);
            };
          }
        } else if (esriGeometryEngine.contains(shape, outletPolyline)) {
          const newGraphic = new Graphic();
          newGraphic.symbol = graphic.symbol;
          newGraphic.geometry = shape;
          newGraphic.attributes = {
            Acres: computeAcres(shape),
            Avg_Slope: graphic.attributes['Avg_Slope'],
            MaxFlowLenghtFt: graphic.attributes['MaxFlowLenghtFt'],
            OBJECTID: !origIdTaken ? graphic.attributes.OBJECTID : ++maxObjectId,
            Shape_Area: esriGeometryEngine.geodesicArea(shape, "square-meters"),
            Shape_Length: esriGeometryEngine.geodesicLength(shape, "meters"),
            Subbasin: graphic.attributes['Subbasin'],
            gridcode: graphic.attributes['gridcade']
          };
          origIdTaken = true;
          this.watershedPolygonLayer.graphics.add(newGraphic, origIndex++);
        }
      }
      this.watershedPolygonLayer.graphics.remove(graphic);
    }
    this.errorReset();
    return;
  }

  protected async onCancelOperation(): Promise<void> {
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
    return;
  }
}
